import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FeesPage.module.css';

const IMG = '/static/images/fees';
const ARROW = '/static/images/landing/hero-cta-arrow.svg';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Content (copy is taken verbatim from the design) ----------

const SELLER_FEES = [
  {
    icon: 'icon-check.svg',
    title: 'List for free',
    body: 'Post your listing and reach thousands of skydivers worldwide without paying anything!',
    label: 'Any Listing',
    value: 'FREE',
  },
  {
    icon: 'icon-tag.svg',
    title: 'Transaction fee',
    body: 'Secure payment, easy payout, constant support. Only charged when you sold.',
    label: 'Sold listing',
    value: '3% of sale price',
  },
];

const BUYER_FEES = [
  {
    icon: 'icon-shield-check.svg',
    title: 'Buyer protection',
    body: 'If the gear is not as described, you are protected and will get a full refund.',
    label: 'Protected purchase',
    value: '3% of sale price',
  },
  {
    icon: 'icon-package.svg',
    title: 'Shipping',
    body:
      'Tracked shipping for all items. This is set and received by the seller and will display as soon as you select the shipping region.',
    label: 'Shipping',
    value: 'Set by the seller',
  },
  {
    icon: 'icon-user.svg',
    title: 'Pickup',
    body:
      'Collecting the item directly from the seller is of course free. Just buy, set a date, and pick it up!',
    label: 'Pickup',
    value: 'Free',
  },
];

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'WebPage',
    name: 'Avento Fees | Transparent fees for buyers and sellers',
    url: `${rootURL}/fees`,
    description:
      'Transparent and fair fees on Avento. Listing is always free, sellers pay a 3% fee only when sold, and buyers get full protection with a 3% buyer protection fee.',
  };
}

// ---------- Cards ----------

const FeeCard = ({ fee }) => (
  <div className={css.feeCard}>
    <div className={css.feeCardHead}>
      <img src={`${IMG}/${fee.icon}`} alt="" className={css.feeIcon} width={24} height={24} />
      <h3 className={css.feeTitle}>{fee.title}</h3>
    </div>
    <p className={css.feeText}>{fee.body}</p>
    <div className={css.feeRow}>
      <span className={css.feeRowLabel}>{fee.label}</span>
      <span className={css.feeRowValue}>{fee.value}</span>
    </div>
  </div>
);

// ---------- Main component ----------

const FeesPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();
  const rootURL = config.marketplaceRootURL || '';

  return (
    <Page
      title="Avento Fees | Transparent fees for buyers and sellers"
      description="Transparent and fair fees on Avento. Listing is always free, sellers pay a 3% fee only when sold, and buyers get full protection with a 3% buyer protection fee."
      schema={buildPageSchema(rootURL)}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="FeesPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Hero — dynamic title overlaid on a text-free image (same technique as the
                    home hero: cqw units against a container-sized full-bleed image). */}
                <section className={css.heroSection}>
                  <picture>
                    <source
                      media="(min-width: 860px)"
                      srcSet={`${IMG}/fees-hero-desktop.webp`}
                      width={3024}
                      height={700}
                    />
                    <img
                      src={`${IMG}/fees-hero-mobile.webp`}
                      alt=""
                      className={css.heroImage}
                      width={840}
                      height={840}
                      fetchpriority="high"
                    />
                  </picture>
                  <div className={css.heroOverlay}>
                    <h1 className={css.heroTitle}>Avento Fees</h1>
                    <p className={css.heroSubtitle}>
                      Transparent and fair fees for sellers and buyers
                    </p>
                  </div>
                </section>

                {/* Seller fees */}
                <section className={css.feesSection}>
                  <div className={css.container}>
                    <div className={css.sectionHead}>
                      <div className={css.sectionHeadText}>
                        <h2 className={css.sectionHeading}>Seller fees</h2>
                        <p className={css.sectionSub}>No hidden fees when selling on Avento</p>
                      </div>
                      <NamedLink className={css.ctaButton} name="NewListingPage">
                        <span>Sell now</span>
                        <img src={ARROW} alt="" className={css.ctaArrow} width={20} height={20} />
                      </NamedLink>
                    </div>
                    <div className={css.divider} />
                    <div className={css.feeGrid}>
                      {SELLER_FEES.map(fee => (
                        <FeeCard key={fee.title} fee={fee} />
                      ))}
                    </div>
                  </div>
                </section>

                {/* Buyer fees */}
                <section className={`${css.feesSection} ${css.feesSectionLast}`}>
                  <div className={css.container}>
                    <div className={css.sectionHead}>
                      <div className={css.sectionHeadText}>
                        <h2 className={css.sectionHeading}>Buyer fees</h2>
                        <p className={css.sectionSub}>Minimal fees for full protection</p>
                      </div>
                      <NamedLink className={css.ctaButton} name="SearchPage">
                        <span>Buy now</span>
                        <img src={ARROW} alt="" className={css.ctaArrow} width={20} height={20} />
                      </NamedLink>
                    </div>
                    <div className={css.divider} />
                    <div className={`${css.feeGrid} ${css.feeGridThree}`}>
                      {BUYER_FEES.map(fee => (
                        <FeeCard key={fee.title} fee={fee} />
                      ))}
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const FeesPage = connect(mapStateToProps)(FeesPageComponent);

export default FeesPage;
